/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { isEmpty } from "@utils/lodash";

import { JobService } from "@services";
import {
	ADDITIONAL_JOB_TYPES,
	JOB_INVOICING_TYPES,
	JOB_PAYMENT_TYPES,
	JOB_INVOICED_TYPES,
	CLIENTS_PAYS_FOR_THE_JOB_OPTIONS,
	ELECTRONICALLY_BILLED_OPTIONS,
} from "@constants";
import utils from "@utils";

import { ReactSelect, Tooltip, Control, Option } from "../../../../../components";
import ExpandableSection from "./ExpandableSection";
import AddClientModal from "../modals/AddClientModal";
import RollKallInvoicingBenefits from "./RollKallInvoicingBenefits";

import "./InvoicingDetails.scss";

const InvoicingDetails = ({
	form,
	user,
	generalForm,
	clientContacts,
	updateForm,
	openQuickAddContactModal,
	validateSection,
	isNewJob,
	expandNextSection,
	positionWithPayCodeExist,
	updateTransferPaymentsToAgency,
	transferPaymentToJurisdictionEnabled,
	transferPaymentsToAgencyRuleEnabled,
	clients,
	clientId,
	loadingClients,
	onlyAuthorizedClientsAreAllowedToUseVehicles,
	requireInvoicingForVehicles,
	jobs,
	openForcedVehicleInvoicingModal,
	confirmationModal,
	jobEditingProperties,
	getClients,
	onClientChange,
	onContinue,
	isInvoicingFirstEnabled,
	loadingSchedulingOptions,
	loadJobInvoiceOnlyOptions,
	enforceV2NonRKPayInvoicingEnabled,
	isClientEnforceV2NonRKPayInvoicing,
	setIsClientEnforceV2NonRKPayInvoicing,
}) => {
	const { lang } = global;
	const prepayEnabledForOrg = user.Features.some(f => f.toLowerCase().includes("prepay"));
	const officerInvoicingV3EnabledForOrg = user.Features.some(f => f.toLowerCase().includes("officerinvoicingv3"));
	const clientBillingContactRef = useRef();
	const [payCodeWasAdded, setPayCodeWasAdded] = useState(positionWithPayCodeExist);
	const [isExcludeOfficerRateCheckboxEnabled, setIsExcludeOfficerRateCheckboxEnabled] =
		useState(false);
	const [forceVehicleInvoicing, setForceVehicleInvoicing] = useState(false);
	const [isTransferPaymentsToAgencyDefault, setIsTransferPaymentsToAgencyDefault] =
		useState(false);
	const [showAddClientModal, setShowAddClientModal] = useState(false);

	// It has to be V3 but since they are viewing this component they are V3 agencies
	const nonRKPayTransferOfficerPaymentsToAgencyEnabled =
		transferPaymentsToAgencyRuleEnabled &&
		!transferPaymentToJurisdictionEnabled &&
		(user.IsInternalCoordinator || user.isLEA());

	const RKPayTransferOfficerPaymentsToAgencyEnabled =
		transferPaymentsToAgencyRuleEnabled &&
		!transferPaymentToJurisdictionEnabled &&
		(user.IsInternalCoordinator || user.isLEA());

	// It has to be V3 but since they are viewing this component they are V3 agencies
	const excludeOfficerRateGeneralConditions =
		user.Organization.VehicleInvoicingEnabled &&
		onlyAuthorizedClientsAreAllowedToUseVehicles &&
		requireInvoicingForVehicles;

	const isClientEnabledToExcludeOfficerRates = client => {
		if (
			!excludeOfficerRateGeneralConditions ||
			!client.AuthorizedToUseDepartmentVehicles ||
			!client.AllowExcludeOfficerRate ||
			client.EnforceIncludeOfficerRate
		) {
			return false;
		}
		return true;
	};

	const validateOfficerInvoicingJob = formToValidate => {
		// Validations for officer invoicing job in NonRKPay infrastructure
		const validateOfficerV3InvoicingJob = () => {
			if (
				!user.isAgencyAdmin() &&
				(
					formToValidate.ClientBillingContactId === "" ||
					formToValidate.ClientBillingContactId === null ||
					isClientEnforceV2NonRKPayInvoicing ||
					!formToValidate.TermsAndConditionsAcknowledge
				)
			) {
				return false;
			}
			return true;
		}

		if (officerInvoicingV3EnabledForOrg) {
			return validateOfficerV3InvoicingJob();
		}

		// Regular validations of officer in RKPay infrastructure
		if (
			!user.isAgencyAdmin() &&
			(
				formToValidate.ClientBillingContactId === "" ||
				formToValidate.ClientBillingContactId === null ||
				!formToValidate.TermsAndConditionsAcknowledge
			)
		) {
			return false;
		}
		return true;
	};

	const validateAgencyInvoicingJob = formToValidate => {

		const validateV3AgencyInvoiceJob = () => {
			if (
				formToValidate.ClientBillingContactId === "" ||
				formToValidate.ClientBillingContactId === null ||
				!formToValidate.TermsAndConditionsAcknowledge
			) {
				return false;
			}
			return true;
		};

		const validateEnforcedV2AgencyInvoiceJob = () => {
			if (
				formToValidate.ClientBillingContactId === "" ||
				formToValidate.ClientBillingContactId === null
			) {
				return false;
			}
			return true;
		};

		if (isClientEnforceV2NonRKPayInvoicing)
			return validateEnforcedV2AgencyInvoiceJob();

		return validateV3AgencyInvoiceJob();
	};

	const isScheduleOnlyClientNotPayValid = formToValidate =>
		clientId !== "" &&
		formToValidate.ClientPaysForTheJob === false &&
		formToValidate.SchedulingJobType !== null;

	const isScheduleOnlyValid = formToValidate =>
		clientId !== "" &&
		formToValidate.ElectronicallyBilled === false &&
		!isEmpty(formToValidate.PaymentDetails?.trim()) &&
		!isEmpty(`${formToValidate.ClientBillingContactId || ""}`) &&
		!isEmpty(formToValidate.AdditionalPaymentMethod) &&
		!isEmpty(formToValidate.NotInvoicingInformation?.trim());

	const validateSchedulingInvoicingJob = formToValidate => {
		if (
				formToValidate.ClientPaysForTheJob == null ||
				(
					formToValidate.ClientPaysForTheJob === false &&
					!formToValidate.SchedulingJobType &&
					formToValidate.SchedulingJobType !== ADDITIONAL_JOB_TYPES.OTHER
				)
				||
				(
					formToValidate.ClientPaysForTheJob &&
					formToValidate.ElectronicallyBilled
				)
				||
				(
					formToValidate.ClientPaysForTheJob &&
					formToValidate.ElectronicallyBilled === null
				)
		) {
			return false;
		}

		if (
			formToValidate.ClientPaysForTheJob === false &&
			!isScheduleOnlyClientNotPayValid(formToValidate)
		) {
			return false;
		}

		if (formToValidate.ElectronicallyBilled === false && !isScheduleOnlyValid(formToValidate)) {
			return false;
		}

		return true;
	};

	const validateForm = newForm => {
		let isValid = true;
		const formToValidate = newForm ? newForm.paymentDetails : form;

		switch (formToValidate.JobInvoicedTypeId) {
			case 1:
				isValid = validateOfficerInvoicingJob(formToValidate);
				break;
			case 2:
				isValid = validateAgencyInvoicingJob(formToValidate);
				break;
			case 3:
				isValid = validateSchedulingInvoicingJob(formToValidate);
				break;
			default:
				isValid = false;
				break;
		}

		validateSection("paymentDetails", isValid);
		return isValid;
	};

	const isVehicleInvoicingForced = jobsList => {
		if (requireInvoicingForVehicles && jobsList.length > 0) {
			return jobsList.some(j => {
				const { VehicleRequired, VehicleTypes } = j;
				return VehicleRequired && VehicleTypes.length > 0;
			});
		}
		return false;
	};

	const handleForceVehicleInvoicingChange = value => {
		setForceVehicleInvoicing(value);
		if (value) {
			const rkpayClient = clients.find(item => item.value === clientId);
			const jobInvoicedTypeId =
				rkpayClient?.JobInvoicedTypeId !== JOB_INVOICED_TYPES.SCHEDULE_ONLY
					? rkpayClient?.JobInvoicedTypeId
					: null;

			updateForm(
				"paymentDetails",
				{
					ClientPaysForTheJob: true,
					ElectronicallyBilled: true,
					JobInvoicedTypeId:
						form.JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY
							? jobInvoicedTypeId
							: form.JobInvoicedTypeId,
					IsScheduling: false,
				},
				true,
				validateForm,
			);
		}
	};

	const handleIsRKPayChange = value => () => {
		if (value === false) {
			updateForm(
				"paymentDetails",
				{
					IsRKPay: value,
					IsScheduling: false,
					JobInvoicedTypeId: 2,
					PaymentMethod: user.Organization.PaymentDetails,
					...(isClientEnforceV2NonRKPayInvoicing && {
						RequirePrepayment: false,
						ExcludeOfficerRate: false,
						WaiveJurisdictionFees: false,
						TransferPaymentsToAgency: false,
						TermsAndConditionsAcknowledge: false,
					}),
				},
				true,
				() => true,
			);
		} else {
			updateForm(
				"paymentDetails",
				{ IsRKPay: officerInvoicingV3EnabledForOrg ? false : value, IsScheduling: false, JobInvoicedTypeId: 1 },
				true,
				() => true,
			);
		}
	};

	const handleOrgFeeBlur = ({ target: { value } }) => {
		const intValue = parseInt(value, 10);
		if (value.indexOf("-") !== -1 || intValue < 0 || Number.isNaN(intValue)) {
			updateForm("paymentDetails", { OrganizationFee: "0" }, true);
		} else {
			validateForm();
		}
	};

	const handleBillingContactChange = event => {
		updateForm(
			"paymentDetails",
			{
				ClientBillingContactId: event?.value || "",
			},
			false,
			validateForm,
		);
	};

	const handleContinue = () => {
		if (form.OrganizationFee === "") {
			updateForm("paymentDetails", { OrganizationFee: "0" }, false);
		} else {
			updateForm("paymentDetails", {
				OrganizationFee: form.OrganizationFee || "0",
				ClientBillingContactId: form.ClientBillingContactId || "",
			});
		}

		const isValid = validateForm();
		expandNextSection("paymentDetails");

		if (isInvoicingFirstEnabled && isValid) {
			onContinue();
		}
	};

	const handleClientPaysForTheJobChange = value => {
		if (forceVehicleInvoicing) {
			openForcedVehicleInvoicingModal();
			return;
		}

		const details = {
			ClientPaysForTheJob: value,
			ElectronicallyBilled: null,
		};

		updateForm(
			"paymentDetails",
			{
				...details,
				IsRKPay: !value ? false : form.IsRKPay,
				JobInvoicedTypeId: !value ? 3 : null,
				IsScheduling: !value, // If client is not paying it's scheduling,
				SchedulingJobType: null,
				AdditionalInformation: null,
				PaymentDetails: null,
				ClientBillingContactId: null,
				AdditionalPaymentMethod: null,
				NotInvoicingInformation: null,
				TermsAndConditionsAcknowledge: null,
			},
			!value,
			validateForm,
		);

		if (!value) {
			loadJobInvoiceOnlyOptions(JOB_INVOICED_TYPES.SCHEDULE_ONLY, clientId, details);
		}
	};

	const handleElectronicallyBilledChange = value => {
		if (forceVehicleInvoicing) {
			openForcedVehicleInvoicingModal();
			return;
		}

		const details = {
			ClientPaysForTheJob: true,
			ElectronicallyBilled: value,
		};

		updateForm(
			"paymentDetails",
			{
				...details,
				IsRKPay: !value ? false : form.IsRKPay,
				JobInvoicedTypeId: !value ? 3 : null,
				IsScheduling: !value, // If it's not billed electronically it's scheduling
				_expandedBenefits: true,
				AdditionalInformation: null,
				PaymentDetails: null,
				ClientBillingContactId: null,
				AdditionalPaymentMethod: null,
				NotInvoicingInformation: null,
				TermsAndConditionsAcknowledge: null,
			},
			!value,
			validateForm,
		);

		if (!value) {
			loadJobInvoiceOnlyOptions(JOB_INVOICED_TYPES.SCHEDULE_ONLY, clientId, details);
		} else {
			const rkpayClient = clients.find(item => item.value === clientId);
			updateForm(
				"paymentDetails",
				{
					JobInvoicedTypeId:
						rkpayClient.JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY
							? null
							: rkpayClient.JobInvoicedTypeId,
					ClientBillingContactId:
						rkpayClient.BillingContactId || rkpayClient.billingContactId || "",
				},
				false,
			);
		}
	};

	/** Displays a confirmation modal prompting the user to modify the Exclude Officer pay checkbox */
	const updateExcludeOfficerRate = value => {
		const updateExcludeProp = () => {
			updateForm("paymentDetails", { ExcludeOfficerRate: !form.ExcludeOfficerRate }, true);
		};

		if (value) {
			confirmationModal.openModal({
				title: lang.t("Exclude Officer Pay on the Invoice"),
				message: lang.t("Exclude officer pay modal message"),
				cancelButton: lang.t("Cancel"),
				confirmButton: lang.t("Yes, exclude officer pay from the invoice"),
				onConfirm: updateExcludeProp,
			});
			return;
		}

		updateExcludeProp();
	};

	const setDefaultSchedulingOptions = async () => {
		try {
			const {
				PaymentDetails,
				NotInvoicingInformation,
				AdditionalPaymentMethod: PaymentMethod,
				ClientBillingContactId: SchedulingBillingContactId,
				SchedulingJobType,
				AdditionalInformation,
				ClientPaysForTheJob,
				ElectronicallyBilled,
			} = form;

			await JobService.upsertSchedulingOnlyOptions({
				ClientId: clientId,
				SchedulingJobType,
				AdditionalInformation,
				PaymentDetails,
				PaymentMethod,
				NotInvoicingInformation,
				SchedulingBillingContactId,
				ClientPaysForTheJob,
				ElectronicallyBilled,
			});

			confirmationModal.openModal({
				title: lang.t("Set Default"),
				message: lang.t("Schedule Only Option Save Message"),
				hideCancel: true,
				hideConfirm: false,
			});
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error saving Scheduling Options"),
				timeOut: 1500,
			});
		}
	};

	const onScheduleOnlyChange = (
		payload,
		clientWillNotBePaying = false,
		clientWillNotBeBilled = false,
	) => {
		const newForm = { ...payload };

		if (clientWillNotBePaying) {
			newForm.PaymentDetails = "";
			newForm.AdditionalPaymentMethod = null;
			newForm.ClientBillingContactId = "";
			newForm.NotInvoicingInformation = "";
		}

		if (clientWillNotBeBilled) {
			newForm.SchedulingJobType = null;
			newForm.AdditionalInformation = "";
		}

		updateForm("paymentDetails", newForm, false);
	};

	const getOfficerInvoicingOptionsTitle = () =>
		(officerInvoicingV3EnabledForOrg && isClientEnforceV2NonRKPayInvoicing) ? lang.t("Job enforceV2 options validation_") : "";

	const updateFormForClientEnforcedV2 = () => {
		const { ClientPaysForTheJob, ElectronicallyBilled, JobInvoicedTypeId } = form;

		updateForm(
			"paymentDetails",
			{
				RequirePrepayment: false,
				TransferPaymentsToAgency: false,
				TermsAndConditionsAcknowledge: false,
				ExcludeOfficerRate: false
			},
			false,
			validateForm,
		);

		if (ClientPaysForTheJob && ElectronicallyBilled != null) {
			updateForm(
				"paymentDetails",
				{
					ElectronicallyBilled: null,
					PaymentMethod: "",
					SchedulingJobType: null,
					AdditionalInformation: "",
					InvoicingType: null,
					PaymentDetails: "",
					AdditionalPaymentMethod: "",
					NotInvoicingInformation: "",
					JobInvoicedTypeId: JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY
					? null
					: JobInvoicedTypeId,
				},
				false,
				validateForm,
			);
		}
	}


	// If we are editing/duplicating a job/draft, validation should update immediately.
	// When creating a new job, we want the user to go through all the steps instead
	useEffect(() => {
		if (
			!isNewJob ||
			(clientBillingContactRef.current &&
				clientBillingContactRef.current !== form.ClientBillingContactId)
		) {
			validateForm();
			// update clientBillingContactRef to match form value
			clientBillingContactRef.current = form.ClientBillingContactId;
		} else if (!clientBillingContactRef.current && form.ClientBillingContactId !== "") {
			clientBillingContactRef.current = form.ClientBillingContactId;
		}
	}, [form]);

	useEffect(() => {
		setPayCodeWasAdded(positionWithPayCodeExist);
		if (positionWithPayCodeExist) {
			updateForm(
				"paymentDetails",
				{
					RequirePrepayment: false,
				},
				false,
			);
		}
	}, [positionWithPayCodeExist]);

	useEffect(() => {
		if (!loadingClients && clientId && clientId !== "") {
			const selectedClient = clients.find(c => c.value === clientId);
			setIsExcludeOfficerRateCheckboxEnabled(
				isClientEnabledToExcludeOfficerRates(selectedClient),
			);
			setIsTransferPaymentsToAgencyDefault(selectedClient.TransferOfficerPaymentsToAgency);

			setIsClientEnforceV2NonRKPayInvoicing(
				enforceV2NonRKPayInvoicingEnabled && !!selectedClient.EnforceV2NonRKPayInvoicing
			);

			return;
		}
		setIsExcludeOfficerRateCheckboxEnabled(false);
		setIsClientEnforceV2NonRKPayInvoicing(false);
	}, [clientId, loadingClients]);

	useEffect(() => {
		validateForm();
		if (isClientEnforceV2NonRKPayInvoicing) {
			updateFormForClientEnforcedV2();
		}
	}, [isClientEnforceV2NonRKPayInvoicing]);

	useEffect(() => {
		const isVehicleInvoicingRequired = isVehicleInvoicingForced(jobs);
		if (
			(forceVehicleInvoicing && !isVehicleInvoicingRequired) ||
			(!forceVehicleInvoicing && isVehicleInvoicingRequired)
		) {
			handleForceVehicleInvoicingChange(isVehicleInvoicingRequired);
		}
	}, [jobs]);

	useEffect(() => {
		getClients();
	}, []);

	useEffect(() => {
		if (
			officerInvoicingV3EnabledForOrg &&
			form.IsRKPay &&
			form.RequirePrepayment
		) {
			updateForm(
				"paymentDetails",
				{
					RequirePrepayment: false
				},
				false
			);
		}
	}, [form.IsRKPay, form.RequirePrepayment, officerInvoicingV3EnabledForOrg]);

	return (
		<div id="payment-details-container">
			<If condition={isInvoicingFirstEnabled}>
				<If condition={jobEditingProperties._userOwnsJob}>
					<div
						className={classNames("row form-group", {
							"invalid-field": generalForm._clientId === "",
						})}
					>
						<div className="col-xs-9">
							<label>{lang.t("Client")}</label>
							<span className="required-mark" />
						</div>
						<div className="col-xs-3 text-right">
							<button
								type="button"
								id="open-add-client-modal"
								className="btn btn-link btn-blue-small"
								onClick={() => setShowAddClientModal(true)}
							>
								{`+ ${lang.t("New")}`}
							</button>
						</div>
						<ReactSelect
							id="client-id"
							className="col-xs-12"
							value={generalForm._clientId}
							name="clientId"
							onChange={onClientChange}
							options={clients}
							isLoading={loadingClients || loadingSchedulingOptions}
							onMenuClose={validateForm}
							isClearable
							dataTestId="jobs__dropdown-client"
						/>
					</div>
				</If>
			</If>

			<If condition={form.ClientPaysForTheJob === null}>
				<div className="row">
					<div className="col-xs-12">
						{lang.t("Is a client going to be paying for this job_")}
					</div>
				</div>
				<div className="row">
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="clientPaysForTheJob_yes">
							<input
								type="radio"
								name="clientPaysForTheJob"
								id="clientPaysForTheJob_yes"
								data-test-id="jobs__client-pays-for-the-job-yes"
								onChange={() => handleClientPaysForTheJobChange(true)}
							/>
							{lang.t("Yes")}
						</label>
					</div>
					<div className="col-xs-4 col-sm-2 radio">
						<label htmlFor="clientPaysForTheJob_no">
							<input
								type="radio"
								name="clientPaysForTheJob"
								id="clientPaysForTheJob_no"
								data-test-id="jobs__client-pays-for-the-job-no"
								onChange={() => handleClientPaysForTheJobChange(false)}
							/>
							{lang.t("No")}
						</label>
					</div>
				</div>
			</If>
			<If condition={form.ClientPaysForTheJob != null}>
				<div className="row">
					<div className="col-xs-12 col-sm-10 form-group slideInUp">
						<ReactSelect
							id="client-paying-id"
							name="clientPaysForTheJob"
							value={form.ClientPaysForTheJob}
							onChange={e => handleClientPaysForTheJobChange(e?.value)}
							options={CLIENTS_PAYS_FOR_THE_JOB_OPTIONS}
							isDisabled={form.RequirePrepayment}
							components={{ Control, Option }}
							dataTestId="jobs__client-pays-for-the-job"
						/>
					</div>
				</div>
			</If>
			<If condition={form.ClientPaysForTheJob === true}>
				<If condition={form.ElectronicallyBilled === null && !isClientEnforceV2NonRKPayInvoicing}>
					<div className="row">
						<div className="col-xs-12">
							{lang.t(
								"Will the client be paying online through the Rollkall platform_",
							)}
						</div>
					</div>
					<div className="row">
						<div className="col-xs-4 col-sm-2 radio">
							<label htmlFor="ElectronicallyBilled_yes">
								<input
									type="radio"
									name="electronicallyBilled"
									id="electronicallyBilled_yes"
									onChange={() => handleElectronicallyBilledChange(true)}
									data-test-id="jobs__electronically-billed-yes"
								/>
								{lang.t("Yes")}
							</label>
						</div>
						<div className="col-xs-4 col-sm-2 radio">
							<label htmlFor="electronicallyBilled_no">
								<input
									type="radio"
									name="electronicallyBilled"
									id="electronicallyBilled_no"
									onChange={() => handleElectronicallyBilledChange(false)}
									data-test-id="jobs__electronically-billed-no"
								/>
								{lang.t("No")}
							</label>
						</div>
					</div>
				</If>
				<If condition={form.ElectronicallyBilled != null}>
					<div className="row">
						<div className="col-xs-12 col-sm-12 form-group slideInUp">
							<ReactSelect
								id="billed-electronically-id"
								name="electronicallyBilled"
								value={form.ElectronicallyBilled}
								onChange={e => handleElectronicallyBilledChange(e?.value)}
								options={ELECTRONICALLY_BILLED_OPTIONS}
								isDisabled={form.RequirePrepayment}
								components={{ Control, Option }}
								dataTestId="jobs__electronically-billed"
							/>
						</div>
					</div>
					<If condition={form.ElectronicallyBilled === false}>
						<div className="slideInUp">
							<ExpandableSection
								title={lang.t(
									"Opting out of electronic invoicing and payments through the RollKall platform means missing out on_",
								)}
								expanded={form._expandedBenefits}
								handleToggle={() =>
									updateForm(
										"paymentDetails",
										{
											_expandedBenefits: !form._expandedBenefits,
										},
										false,
									)
								}
								bodyClassName="benefits-expandable-section"
							>
								<RollKallInvoicingBenefits />
							</ExpandableSection>
						</div>

						<div className="row form-group flex flex-direction-column">
							<div className="col-xs-12">
								<label className="control-label">
									{lang.t("What type of invoicing do you want to use_")}
									<span className="required-mark" />
								</label>
								<div className="col-xs-12 radio padding-left-0">
									<label htmlFor="scheduleOnly">
										<input
											type="radio"
											name="SchedulingJobType"
											id="scheduleOnly"
											value={JOB_INVOICING_TYPES.SCHEDULE_ONLY}
											checked
										/>
										{lang.t("Scheduling Only")}
									</label>
								</div>
							</div>
						</div>

						<div className="row form-group">
							<div
								className={classNames("col-xs-12", {
									"invalid-field": isEmpty(form.PaymentDetails?.trim()),
								})}
							>
								<label
									htmlFor="paymentDetails"
									className="control-label"
								>
									{lang.t("Payment Details")}
									<span className="required-mark" />
								</label>
								<textarea
									id="paymentDetails"
									name="paymentDetails"
									className="form-control"
									value={form.PaymentDetails}
									maxLength={500}
									onBlur={() =>
										onScheduleOnlyChange({
											PaymentDetails: form.PaymentDetails?.trim() || "",
										})
									}
									onChange={({ target: { value } }) =>
										onScheduleOnlyChange({ PaymentDetails: value }, false, true)
									}
									data-test-id="jobs__payment-details"
								/>
							</div>
						</div>

						<div className="row form-group margin-bottom-0">
							<div
								className={classNames("col-xs-12", {
									"invalid-field": isEmpty(
										`${form.ClientBillingContactId || ""}`,
									),
								})}
							>
								<div className="col-xs-6 col-sm-4 padding-left-0">
									<label className="control-label">
										{lang.t("Billing Contact")}
										<span className="required-mark" />
									</label>
								</div>
								<div className="col-xs-6 col-sm-2 text-right">
									<button
										type="button"
										className="btn btn-link open-add-contact-modal btn-blue-small"
										onClick={() => openQuickAddContactModal("contacts")}
										data-test-id="jobs__open-add-contact-modal"
									>
										{`+ ${lang.t("New")}`}
									</button>
								</div>
							</div>
						</div>
						<div className="row form-group">
							<div
								className={classNames("col-xs-12 col-sm-6 form-group", {
									"invalid-field": isEmpty(
										`${form.ClientBillingContactId || ""}`,
									),
								})}
							>
								<ReactSelect
									id="client-billing-contact-id"
									name="ClientBillingContactId"
									value={form.ClientBillingContactId}
									onChange={({ value }) =>
										onScheduleOnlyChange(
											{ ClientBillingContactId: value },
											false,
											true,
										)
									}
									options={clientContacts}
									isClearable
									dataTestId="jobs__client-billing-contact-id"
								/>
							</div>
						</div>

						<div className="row form-group">
							<div
								className={classNames("col-xs-12", {
									"invalid-field": isEmpty(form.AdditionalPaymentMethod),
								})}
							>
								<label className="control-label">
									{lang.t("How will this job get paid_")}
									<span className="required-mark" />
								</label>
							</div>
							<div className="col-xs-12 radio">
								<label htmlFor="cash">
									<input
										type="radio"
										name="HowJobGetPaid"
										id="cash"
										value={JOB_PAYMENT_TYPES.CASH.value}
										checked={
											form.AdditionalPaymentMethod ===
											JOB_PAYMENT_TYPES.CASH.value
										}
										onChange={({ target: { value } }) =>
											onScheduleOnlyChange(
												{ AdditionalPaymentMethod: value },
												false,
												true,
											)
										}
										data-test-id="jobs__cash"
									/>
									{JOB_PAYMENT_TYPES.CASH.label}
								</label>
							</div>
							<div className="col-xs-12 radio">
								<label htmlFor="check">
									<input
										type="radio"
										name="HowJobGetPaid"
										id="check"
										value={JOB_PAYMENT_TYPES.CHECK.value}
										checked={
											form.AdditionalPaymentMethod ===
											JOB_PAYMENT_TYPES.CHECK.value
										}
										onChange={({ target: { value } }) =>
											onScheduleOnlyChange(
												{ AdditionalPaymentMethod: value },
												false,
												true,
											)
										}
										data-test-id="jobs__check"
									/>
									{JOB_PAYMENT_TYPES.CHECK.label}
								</label>
							</div>
							<div className="col-xs-12 radio">
								<label htmlFor="digitalwallet">
									<input
										type="radio"
										name="HowJobGetPaid"
										id="digitalwallet"
										value={JOB_PAYMENT_TYPES.OTHER.value}
										checked={
											form.AdditionalPaymentMethod ===
											JOB_PAYMENT_TYPES.OTHER.value
										}
										onChange={({ target: { value } }) =>
											onScheduleOnlyChange(
												{ AdditionalPaymentMethod: value },
												false,
												true,
											)
										}
										data-test-id="jobs__digitalwallet"
									/>
									{JOB_PAYMENT_TYPES.OTHER.label}
								</label>
							</div>
						</div>

						<div className="row form-group">
							<div
								className={classNames("col-xs-12", {
									"invalid-field": isEmpty(form.NotInvoicingInformation?.trim()),
								})}
							>
								<label
									htmlFor="notRollkallDetails"
									className="control-label"
								>
									{lang.t(
										"Please share the reason for not utilizing the RollKall platform for online invoicing_",
									)}
									<span className="required-mark" />
								</label>
								<textarea
									id="notRollkallDetails"
									name="notRollkallDetails"
									className="form-control"
									value={form.NotInvoicingInformation}
									maxLength={500}
									onBlur={() =>
										onScheduleOnlyChange({
											NotInvoicingInformation:
												form.NotInvoicingInformation.trim(),
										})
									}
									onChange={({ target: { value } }) =>
										onScheduleOnlyChange(
											{ NotInvoicingInformation: value },
											false,
											true,
										)
									}
									data-test-id="jobs__not-rollkall-details"
								/>
							</div>
						</div>
					</If>
				</If>
				<If condition={(form.ElectronicallyBilled === true || (form.ElectronicallyBilled === null && isClientEnforceV2NonRKPayInvoicing))}>
					<div className="row">
						<div
							className={classNames("col-xs-12 form-group", {
								"invalid-field": !form.JobInvoicedTypeId,
							})}
						>
							<label>{lang.t("Who will create the invoice_")}</label>
							<span className="required-mark" />
						</div>
					</div>
					<div className="row form-group payment-details-formgroup">
						<div className="radio col-xs-4 col-sm-3">
							<label
								htmlFor="isRKPay_true"
								title={
									payCodeWasAdded
										? lang.t(
											"To enable Officer Invoicing, you must remove paycodes from all positions",
										)
										: getOfficerInvoicingOptionsTitle()
								}
							>
								<input
									type="radio"
									name="isRKPay"
									id="isRKPay_true"
									title={getOfficerInvoicingOptionsTitle()}
									onChange={handleIsRKPayChange(true)}
									checked={form.JobInvoicedTypeId === 1}
									disabled={officerInvoicingV3EnabledForOrg && isClientEnforceV2NonRKPayInvoicing}
									data-test-id="jobs__is-rk-pay-true"
								/>
								{lang.t("Officer")}
							</label>
						</div>
						<div className="col-xs-4 col-sm-3 radio">
							<label
								htmlFor="isRKPay_false"
								className={form.RequirePrepayment ? "item-disabled" : ""}
							>
								<input
									type="radio"
									name="isRKPay"
									id="isRKPay_false"
									onChange={handleIsRKPayChange(false)}
									checked={form.JobInvoicedTypeId === 2}
									disabled={form.RequirePrepayment}
									data-test-id="jobs__is-rk-pay-false"
								/>
								{lang.t("Agency")}
							</label>
						</div>
					</div>
					<If
						condition={
							form.JobInvoicedTypeId !== 3 && (user.isCoordinator() || user.isLEA())
						}
					>
						<div className="row">
							<div className="col-xs-12">
								<label>{lang.t("Set Coordination Fee")}</label>
							</div>
							<div className="col-xs-12 col-sm-6 form-group">
								<div className="coordination-fee">
									<input
										type="number"
										className="form-control"
										value={form.OrganizationFee}
										name="jobOrganizationFee"
										id="jobOrganizationFee"
										onChange={({ target: { value } }) =>
											updateForm(
												"paymentDetails",
												{
													OrganizationFee: value,
												},
												true,
											)
										}
										onBlur={event => handleOrgFeeBlur(event)}
										placeholder="$"
										min="0"
										data-test-id="jobs__job-organization-fee"
									/>
									<p>/ Hour</p>
								</div>
							</div>
						</div>
						<div className="row">
							<div className="col-xs-6 col-sm-4">
								<label>
									{lang.t("Billing Contact")}
									<span className="required-mark" />
								</label>
							</div>
							<div className="col-xs-6 col-sm-2 text-right">
								<button
									type="button"
									className="btn btn-link open-add-contact-modal btn-blue-small"
									onClick={() => openQuickAddContactModal("contacts")}
									data-test-id="jobs__open-add-contact-modal"
								>
									{`+ ${lang.t("New")}`}
								</button>
							</div>
						</div>

						<div className="row">
							<div
								className={classNames("col-xs-12 col-sm-6 form-group", {
									"invalid-field":
										!user.isAgencyAdmin() &&
										(form.IsRKPay || form.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING) &&
										form.ClientBillingContactId === "",
								})}
							>
								<ReactSelect
									id="client-billing-contact-id"
									name="ClientBillingContactId"
									value={form.ClientBillingContactId}
									onChange={handleBillingContactChange}
									options={clientContacts}
									isClearable
									data-test-id="jobs__client-billing-contact-id"
								/>
							</div>
						</div>
					</If>
					<If
						condition={
							prepayEnabledForOrg &&
							form.JobInvoicedTypeId === 1 &&
							(user.isLEA() || user.isCoordinator()) &&
							!officerInvoicingV3EnabledForOrg
						}
					>
						<div className="row">
							<div className="col-xs-12 form-group payment-details-formgroup">
								<label
									htmlFor="requirePrepay"
									className={form.JobInvoicedTypeId !== 1 ? "invisible" : ""}
									title={
										payCodeWasAdded
											? lang.t(
												"To enable Officer Invoicing, you must remove paycodes from all positions",
											)
											: ""
									}
								>
									<input
										type="checkbox"
										id="requirePrepay"
										className="payment-details-checkbox"
										checked={form.RequirePrepayment}
										onChange={() =>
											updateForm(
												"paymentDetails",
												{
													RequirePrepayment: !form.RequirePrepayment,
												},
												false,
											)
										}
										disabled={payCodeWasAdded}
										data-test-id="jobs__require-prepay"
									/>
									{lang.t("Require Prepay")}
								</label>
							</div>
						</div>
					</If>
					<If
						condition={
							form.JobInvoicedTypeId === 2 && isExcludeOfficerRateCheckboxEnabled && !isClientEnforceV2NonRKPayInvoicing
						}
					>
						<div className="row">
							<div className="col-xs-12 form-group payment-details-formgroup">
								<input
									type="checkbox"
									id="exclude-officer-rate"
									className="payment-details-checkbox"
									checked={form.ExcludeOfficerRate === true}
									onChange={({ target: { checked } }) =>
										updateExcludeOfficerRate(checked)
									}
								/>
								<span>{lang.t("Exclude officer pay from vehicle invoices")}</span>
							</div>
						</div>
						<If condition={form.ExcludeOfficerRate === true}>
							<ExpandableSection
								title={lang.t(
									"Opting out of electronic invoicing and payments through the RollKall platform means missing out on_",
								)}
								expanded={form._expandedBenefits}
								handleToggle={() =>
									updateForm(
										"paymentDetails",
										{
											_expandedBenefits: !form._expandedBenefits,
										},
										false,
									)
								}
								bodyClassName="benefits-expandable-section"
							>
								<RollKallInvoicingBenefits />
							</ExpandableSection>
						</If>
					</If>
					<If condition={user.isLEA() && form.JobInvoicedTypeId !== 3}>
						<div className="row">
							<div className="col-xs-12 form-group payment-details-formgroup">
								<input
									type="checkbox"
									name="waiveJurisdictionFees"
									id="waiveJurisdictionFees"
									className="payment-details-checkbox"
									checked={form.WaiveJurisdictionFees}
									onChange={() =>
										updateForm(
											"paymentDetails",
											{
												WaiveJurisdictionFees: !form.WaiveJurisdictionFees,
											},
											true,
										)
									}
									onBlur={() => validateForm()}
									data-test-id="jobs__waive-jurisdiction-fees"
								/>
								{lang.t("Waive Jurisdiction Fees")}
							</div>
						</div>
					</If>
					<If
						condition={
							(
								(form.IsRKPay && RKPayTransferOfficerPaymentsToAgencyEnabled) ||
								(!form.IsRKPay && nonRKPayTransferOfficerPaymentsToAgencyEnabled)
							) &&
							form.JobInvoicedTypeId !== 3 &&
							(
								!isClientEnforceV2NonRKPayInvoicing ||
								// When officerInvoicingV3EnabledForOrg is not enabled we show transfer to agency option since
								// it will be a regular RKPay Job despite client enforce v2.
								(!officerInvoicingV3EnabledForOrg && form.JobInvoicedTypeId === 1)
							)
						}
					>
						<div className="row">
							<div className="col-xs-12 form-group payment-details-formgroup">
								<input
									type="checkbox"
									name="job-transfer-payments"
									id="job-transfer-payments"
									data-test-id="job__transfer-payments"
									className="payment-details-checkbox"
									defaultChecked={isTransferPaymentsToAgencyDefault}
									checked={form.TransferPaymentsToAgency}
									onChange={({ target: { checked } }) =>
										updateTransferPaymentsToAgency(checked)
									}
									onBlur={() => validateForm()}
								/>
								<span>{lang.t("Transfer officer payments to agency")}</span>
								<Tooltip
									content={lang.t(
										"The full amount of the invoice will be paid to the agency instead of being distributed to the police officers_",
									)}
								>
									<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</div>
						</div>
					</If>
					<If condition={
						(
							!isClientEnforceV2NonRKPayInvoicing ||
							// When officerInvoicingV3EnabledForOrg is not enabled we show terms and conditions since
							// it will be a regular RKPay Job despite client enforce v2.
							(!officerInvoicingV3EnabledForOrg && form.JobInvoicedTypeId === 1)
						)
					}>
						<div className="row">
							<div className="col-xs-12 form-group payment-details-formgroup">
								<label
									className="torch-text"
									htmlFor="TermsAndConditionsAcknowledge"
								>
									<input
										type="checkbox"
										id="terms-and-conditions-acknowledged"
										className="payment-details-checkbox"
										checked={form.TermsAndConditionsAcknowledge === true}
										onChange={() =>
											updateForm(
												"paymentDetails",
												{
													TermsAndConditionsAcknowledge:
														!form.TermsAndConditionsAcknowledge,
												},
												false,
											)
										}
										data-test-id="jobs__terms-and-conditions-acknowledged"
									/>
									{lang.t(
										"I acknowledge that the client is aware that they will be electronically invoiced for this job and is required to pay for all services rendered to them",
									)}
								</label>
							</div>
						</div>
					</If>
					<If
						condition={
							form.JobInvoicedTypeId === 1 && isExcludeOfficerRateCheckboxEnabled
						}
					>
						<div className="col-xs-12 form-group payment-note-label">
							<div className="row">
								<span>
									{lang.t(
										"The ability to exclude officer pay on the invoice is only available for Agency Invoicing_",
									)}
								</span>
							</div>
						</div>
					</If>
				</If>
			</If>
			<If condition={form.ClientPaysForTheJob === false}>
				<div className="row flex flex-direction-column">
					<div
						className={classNames("col-xs-12", {
							"invalid-field":
								!form.SchedulingJobType &&
								form.SchedulingJobType !== ADDITIONAL_JOB_TYPES.OTHER,
						})}
					>
						<label>
							{lang.t("What kind of job is this_")}
							<span className="required-mark" />
						</label>
					</div>
					<div className="col-xs-12 radio">
						<label htmlFor="ondutyshift">
							<input
								type="radio"
								name="SchedulingJobType"
								id="ondutyshift"
								value={ADDITIONAL_JOB_TYPES.ON_DUTY_SHIFT}
								checked={
									form.SchedulingJobType === ADDITIONAL_JOB_TYPES.ON_DUTY_SHIFT
								}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{
											SchedulingJobType: Number(value),
											AdditionalInformation: "",
										},
										true,
										false,
									)
								}
								data-test-id="jobs__on-duty-shift"
							/>
							{lang.t("On-duty Shift")}
						</label>
					</div>
					<div className="col-xs-12 radio">
						<label htmlFor="nonprofit">
							<input
								type="radio"
								name="SchedulingJobType"
								id="nonprofit"
								value={ADDITIONAL_JOB_TYPES.NON_PROFIT}
								checked={form.SchedulingJobType === ADDITIONAL_JOB_TYPES.NON_PROFIT}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{
											SchedulingJobType: Number(value),
											AdditionalInformation: "",
										},
										true,
										false,
									)
								}
								data-test-id="jobs__non-profit"
							/>
							{lang.t("Non Profit")}
						</label>
					</div>
					<div className="col-xs-12 radio">
						<label htmlFor="grant">
							<input
								type="radio"
								name="SchedulingJobType"
								id="grant"
								value={ADDITIONAL_JOB_TYPES.GRANT}
								checked={form.SchedulingJobType === ADDITIONAL_JOB_TYPES.GRANT}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{
											SchedulingJobType: Number(value),
											AdditionalInformation: "",
										},
										true,
										false,
									)
								}
								data-test-id="jobs__grant"
							/>
							{lang.t("Grant")}
						</label>
					</div>
					<div className="col-xs-12 radio">
						<label htmlFor="overtime">
							<input
								type="radio"
								name="SchedulingJobType"
								id="overtime"
								value={ADDITIONAL_JOB_TYPES.OVERTIME}
								checked={form.SchedulingJobType === ADDITIONAL_JOB_TYPES.OVERTIME}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{
											SchedulingJobType: Number(value),
											AdditionalInformation: "",
										},
										true,
										false,
									)
								}
								data-test-id="jobs__overtime"
							/>
							{lang.t("Overtime")}
						</label>
					</div>
					<div className="col-xs-12 radio">
						<label htmlFor="other">
							<input
								type="radio"
								name="SchedulingJobType"
								id="other"
								value={ADDITIONAL_JOB_TYPES.OTHER}
								checked={form.SchedulingJobType === ADDITIONAL_JOB_TYPES.OTHER}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{
											SchedulingJobType: Number(value),
											AdditionalInformation: "",
										},
										true,
										false,
									)
								}
								data-test-id="jobs__other"
							/>
							{lang.t("Other")}
						</label>
					</div>
					<If condition={form.SchedulingJobType === ADDITIONAL_JOB_TYPES.OTHER}>
						<div className="form-group padding-left-15 padding-right-15">
							<textarea
								id="OtherDetails"
								name="OtherDetails"
								className="form-control"
								placeholder={lang.t("Please provide details")}
								value={form.AdditionalInformation}
								maxLength={500}
								onBlur={() =>
									onScheduleOnlyChange({
										AdditionalInformation: form.AdditionalInformation.trim(),
									})
								}
								onChange={({ target: { value } }) =>
									onScheduleOnlyChange(
										{ AdditionalInformation: value },
										true,
										false,
									)
								}
								data-test-id="jobs__other-details"
							/>
						</div>
					</If>
				</div>
			</If>
			<If condition={isScheduleOnlyClientNotPayValid(form) || isScheduleOnlyValid(form)}>
				<div className="text-center">
					<button
						type="button"
						id="payment-set-default"
						onClick={() => setDefaultSchedulingOptions()}
						className="btn btn-default btn-medium"
						data-test-id="jobs__payment-set-default"
					>
						<i className="glyphicon glyphicon-saved" />
						&nbsp;{lang.t("Set Default")}
					</button>
				</div>
			</If>
			<If
				condition={
					(
						form.ClientPaysForTheJob === false &&
						(
							!!form.SchedulingJobType ||
							form.SchedulingJobType === ADDITIONAL_JOB_TYPES.OTHER
						)
					)
					||
					(
						form.ClientPaysForTheJob &&
						form.ElectronicallyBilled === false
					)
					||
					(
						form.ElectronicallyBilled &&
						(
							form.JobInvoicedTypeId === 1 ||
							form.JobInvoicedTypeId === 2
						)
					)
					||
					(
						form.ClientPaysForTheJob &&
						form.ElectronicallyBilled == null &&
						isClientEnforceV2NonRKPayInvoicing
					)
				}
			>
				<div className="text-center continue-button-container">
					<button
						type="button"
						id="payment-details-continue"
						onClick={() => handleContinue()}
						className="btn btn-primary btn-big"
						data-test-id="jobs__payment-details-continue"
					>
						{lang.t("Continue")}
					</button>
				</div>
			</If>

			<AddClientModal
				show={showAddClientModal}
				user={user}
				onCancel={() => setShowAddClientModal(false)}
				onSave={() => {
					setShowAddClientModal(false);
					getClients();
				}}
			/>
		</div>
	);
};

InvoicingDetails.propTypes = {
	form: PropTypes.object.isRequired,
	generalForm: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
	updateForm: PropTypes.func.isRequired,
	validateSection: PropTypes.func.isRequired,
	expandNextSection: PropTypes.func.isRequired,
	openQuickAddContactModal: PropTypes.func.isRequired,
	isNewJob: PropTypes.bool,
	clientContacts: PropTypes.array,
	positionWithPayCodeExist: PropTypes.bool,
	updateTransferPaymentsToAgency: PropTypes.func.isRequired,
	transferPaymentToJurisdictionEnabled: PropTypes.bool.isRequired,
	transferPaymentsToAgencyRuleEnabled: PropTypes.bool.isRequired,
	clients: PropTypes.array,
	clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	loadingClients: PropTypes.bool.isRequired,
	onlyAuthorizedClientsAreAllowedToUseVehicles: PropTypes.bool.isRequired,
	requireInvoicingForVehicles: PropTypes.bool.isRequired,
	jobs: PropTypes.array.isRequired,
	openForcedVehicleInvoicingModal: PropTypes.func.isRequired,
	confirmationModal: PropTypes.object,
	jobEditingProperties: PropTypes.object.isRequired,
	getClients: PropTypes.func.isRequired,
	onClientChange: PropTypes.func.isRequired,
	onContinue: PropTypes.func.isRequired,
	isInvoicingFirstEnabled: PropTypes.bool,
	loadingSchedulingOptions: PropTypes.bool.isRequired,
	loadJobInvoiceOnlyOptions: PropTypes.func.isRequired,
	enforceV2NonRKPayInvoicingEnabled: PropTypes.bool.isRequired,
	isClientEnforceV2NonRKPayInvoicing: PropTypes.bool.isRequired,
	setIsClientEnforceV2NonRKPayInvoicing: PropTypes.func.isRequired,
};

InvoicingDetails.defaultProps = {
	isNewJob: false,
	clientContacts: [],
	positionWithPayCodeExist: false,
	clients: [],
	confirmationModal: undefined,
	isInvoicingFirstEnabled: false,
};

export default InvoicingDetails;
